import Raven from "raven-js";
import Base from "nexia_mobile/platforms/base";

export default class Win10Platform extends Base {
  constructor(...args) {
    super(...args);
    this._callbacks = {};
    this._storedValues = {};
  }

  initScriptInterface(callback) {
    this._callNative("GetAllStoredValues", (values) => {
      try {
        this._storedValues = JSON.parse(values);
      } catch (error) {
        Raven.captureException(error, {
          extra: {
            values,
          },
        });
      }
      this._callNative("SetPlatformAttributes", callback);
    });
  }

  exitApp() {
    return this._callNative("Exit");
  }

  getStoredValue(key) {
    return this._storedValues[key];
  }

  setStoredValue(key, value) {
    this._storedValues[key] = value;
    return this._callNative("SetStoredValue", null, key, value);
  }

  clearStoredValue(key) {
    delete this._storedValues[key];
    return this._callNative("ClearStoredValue", null, key);
  }

  clearStoredValues() {
    this._storedValues = {};
    return this._callNative("ClearStoredValues", null);
  }

  isMobilePlatform() {
    switch (this.deviceFamily) {
      case "Phone":
      case "Tablet":
        return true;
      default:
        return false;
    }
  }

  isImageSaveSupported() {
    return true;
  }

  saveImage(base64Data) {
    return this._callBridge("SaveBase64Image", base64Data).then((message) => ({
      hasPermission: true,
      message,
    }));
  }

  obtainPushNotificationToken() {
    return this._callNative("ObtainPushNotificationToken", null);
  }

  isGeofencingSupported() {
    return true;
  }

  checkLocationPermissions() {
    return this._callBridge("CheckLocationPermissions").then(
      (response) => response.result
    );
  }

  openAppPermissionSettings() {
    // Win10 doesn't have one page that shows permission settings for the whole app.
    // Assuming you want to change the location settings for the app.
    this._callBridge("OpenLocationServiceSettings");
  }

  areLocationServicesEnabled() {
    // There's no way to tell if location services are disabled on windows 10,
    // but if they are, then checkLocationPermissions will return false. Since
    // we only use areLocationServicesEnabled in conjunction with checkLocationPermissions,
    // return true here and let checkLocationPermissions handle stuff.
    return Promise.resolve(true);
  }

  openLocationServiceSettings() {
    this._callBridge("OpenLocationServiceSettings");
  }

  setGeofences(geofences) {
    const filteredGeofences = geofences.map((geofence) =>
      _.pick(
        geofence,
        "id",
        "name",
        "latitude",
        "longitude",
        "radius",
        "hrefAcknowledge",
        "hrefEnter",
        "hrefExit"
      )
    );
    let geofencesJsonString;
    try {
      geofencesJsonString = JSON.stringify(filteredGeofences);
    } catch (error) {
      throw new Error(`Failed to stringify geofences: ${filteredGeofences}`);
    }
    return this._callBridge("SetGeofences", geofencesJsonString);
  }

  startGeofenceService() {
    return this._callBridge("StartGeofenceService");
  }

  stopGeofenceService() {
    return this._callBridge("StopGeofenceService");
  }

  getGeofenceCrossings() {
    return this._callBridge("GetGeofenceCrossings");
  }

  sendGoogleAnalyticsEvent(category, action, label) {
    return this._callBridge(
      "SendGoogleAnalyticsEvent",
      category,
      action,
      label
    );
  }

  sendGoogleAnalyticsDuration(category, action, label, duration) {
    return this._callBridge(
      "SendGoogleAnalyticsDuration",
      category,
      action,
      label,
      duration
    );
  }

  // Wifi Methods

  // Does the platform allow programmatic access to Wifi networks?
  canGetWifiNetworks() {
    return true;
  }

  // Does the platform allow you to programmatically connect to a Wifi network?
  canConnectToWifiNetwork() {
    return true;
  }

  // Start a Wifi network scan. The SSID of each found network will be passed
  // to callback as an array of strings.
  wifiScan(callback) {
    return this._callNative("WifiScan", callback);
  }

  // Connect to a Wifi network.
  connectToWifiNetwork(ssid, password) {
    return new Promise((resolve) => {
      this._callNative("ConnectToWifiNetwork", null, ssid, password);
      resolve();
    });
  }

  _callBridge(command, ...args) {
    return new Promise((resolve, reject) => {
      const parseResult = (result) => {
        if (result == null) {
          resolve();
          return;
        }
        let value;
        try {
          value = JSON.parse(result || "");
        } catch (error) {
          Raven.captureException(error, {
            extra: {
              command,
              arguments: args,
              result,
            },
          });
          reject(error);
          return;
        }
        resolve(value);
      };

      this._callNative(command, parseResult, ...args);
    });
  }

  _callNative(command, callback, ...args) {
    let params = {
      name: command,
      args: _.map(args, (arg) => {
        if (arg === null) {
          return "";
        } else {
          return arg;
        }
      }),
    };

    // store the callback so the native code can invoke it later
    if (_.isFunction(callback)) {
      let id = _.uniqueId();
      params.callback = `callback_${id}`;
      this._callbacks[params.callback] = callback;
    }

    const argsString = JSON.stringify(params);

    Raven.captureBreadcrumb({
      category: "Win10 Native",
      level: "info",
      message: command,
      data: {
        args:
          argsString.length > 1024
            ? "[args hidden because size exceeded 1024 bytes]"
            : argsString,
      },
    });

    // pass the command up to the native host
    // when finished, it will invoke our callback by its id
    return window.external.notify(argsString);
  }

  _invokeCallback(id, value) {
    if (!this._callbacks[id]) {
      return;
    }
    if (value) {
      const decodedValue = decodeURIComponent(value);
      this._callbacks[id](decodedValue);
    } else {
      this._callbacks[id]();
    }
    delete this._callbacks[id];
  }
}



// WEBPACK FOOTER //
// ./src/platforms/win10.js